import React from 'react';
import { BEM } from '@feedfm/feed-util';

import './ErrorDisplay.scss';
import { environment } from '~/util/environment';
import { isApolloError } from '@apollo/client';
import { ErrorDisplayApolloError } from '~/components/ErrorDisplay/ErrorDisplayApolloError';

const bem = new BEM( 'ops-error-display', [] );

export interface ErrorDisplayProps {
  /**
   * The error to display. This can be any type. The component is optimized for (in order):
   * - ApolloError
   * - Error
   * - Anything with a `message` property
   * - A simple string
   */
  error: any;
  /**
   * Typical behavior is to key off of appMode to automatically enable development mode. This
   * usually is what you want. Use this prop to override it to off when you are doing local
   * development but want to preview production behavior.
   */
  disableDevelopmentMode?: boolean;
}

/**
 * This is a simple component for rendering an error of any type. It will render extra details in
 * development mode.
 */
export const ErrorDisplay = ( props: ErrorDisplayProps ) => {
  const { disableDevelopmentMode, error } = props;

  if ( !error ) {
    return 'Unknown error';
  }

  if ( isApolloError( error ) ) {
    return <ErrorDisplayApolloError disableDevelopmentMode={ disableDevelopmentMode } error={ error } />;
  }

  if ( error instanceof Error ) {
    return <ErrorDisplayError disableDevelopmentMode={ disableDevelopmentMode } error={ error } />;
  }

  if ( error?.message ) {
    return error.message;
  }

  if ( typeof error === 'string' ) {
    return error;
  }

  return 'Unknown error';
};

ErrorDisplay.displayName = 'ErrorDisplay';
ErrorDisplay.bem = bem;

const ErrorDisplayError = ( { error, disableDevelopmentMode }: { error: Error, disableDevelopmentMode?: boolean } ) => {
  const stack = error.stack || '';

  return (
    <div className={ BEM.$( bem ) }>
      <div className={ BEM.$( bem.el( 'message' ) ) }>{ error.message }</div>

      { disableDevelopmentMode !== true
        && environment.appMode === 'development'
        && <div className={ BEM.$( bem.el( 'stack' ) ) }>{ stack }</div>
      }
    </div>
  );
};

ErrorDisplayError.displayName = 'ErrorDisplayError';
